import {
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  OnDestroy,
  ViewEncapsulation,
} from "@angular/core";
import * as Highcharts from "highcharts";
import { Subscription } from "rxjs";
import { AppService } from "src/app/app.service";
import { PharmaDataService } from "../pharma-data.service";
import {
  ColumnRangeChartOptions,
  DrugsByPhaseApiResponse,
  GlobalHighchartOptions,
  ChartStateColors,
} from "../pharma-page.model";
import { VAIconRegistryService, infoCircle } from "@va/core-library/icons";
import { Store } from "@ngrx/store";
import { FilterEventChanged } from "src/app/store/actions/pharma-page.actions";
import { AppState, stateGetter } from "src/app/store/state/app.state";

@Component({
  selector: "app-drugs-by-phase",
  templateUrl: "./drugs-by-phase.component.html",
  styleUrls: ["./drugs-by-phase.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DrugsByPhaseComponent implements OnInit, OnDestroy {
  chartFilters;
  facetFilters;
  highcharts = Highcharts;
  chartOptions: Highcharts.Options;
  chartReady = false;
  showError = false;
  errorMessage = "";
  updateFlag = false;
  chart: Highcharts.Chart;
  totalCount = 0;
  tickersInfo: any;
  selectedRank = 0;
  lastCalled: any = {};
  categoryToIdMap = {};
  dataCallSubscription: Subscription;
  pharmaDatabasePageSubscriber: Subscription;
  gridSettings;
  @Output() onChartInstance: EventEmitter<Highcharts.Chart> =
    new EventEmitter();

  constructor(
    private service: PharmaDataService,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
    private store: Store<AppState>,
  ) {
    this.iconRegistry.registerIcons([infoCircle]);
  }
  // removed $event here, since it was breaking in build
  @HostListener("window:resize", [])
  sizeChange() {
    this.updateFlag = true;
    if (this.chart) {
      setTimeout(() => {
        this.onChartInstance.emit(this.chart);
      }, 50);
    }
  }

  ngOnInit() {
    Highcharts.setOptions(GlobalHighchartOptions);
    this.service.onGlobalGridSettings.subscribe((resp) => {
      this.gridSettings = resp;
    });
    this.pharmaDatabasePageSubscriber = this.store
      .select(stateGetter("pharmaDatabasePage"))
      .subscribe((data: any) => {
        this.chartFilters = data.chartFilters;
        this.facetFilters = data.facetFilters;
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged === "chart" &&
          !data.filterEvent.source.includes("phase")
        ) {
          this.facetFilters = data.lastFacetFilter;
        }
        if (
          data.filterEvent.filterChanged &&
          data.filterEvent.filterChanged !== "grid" &&
          data.filterEvent.source !== "phase-selected"
        ) {
          this.chartReady = false;
          this.getChartData();
        }
      });
  }

  callGetChartData(currentChartFilters) {
    if (
      this.appService.compare(currentChartFilters, this.lastCalled.chart_filter)
    )
      return false;
    return true;
  }

  getChartData() {
    const params = {
      chart_filter: { ...this.chartFilters },
      filter: { ...this.facetFilters },
      revenue_type: this.gridSettings
        ? this.gridSettings.options.data.revenue_type
        : "total_revenue",
    };
    this.lastCalled = params;
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    this.dataCallSubscription = this.service
      .getDrugsByPhase(params)
      .subscribe((response: DrugsByPhaseApiResponse) => {
        if (response.status === "success") {
          this.convertDataToChartObj(response.data);
          this.showError = false;
          this.categoryToIdMap = response.data["values_mtag_map"];
        } else {
          this.errorMessage = response.message;
          this.showError = true;
        }

        setTimeout(() => {
          this.chartReady = true;
        }, 0);
      });
  }

  convertDataToChartObj(dataObj) {
    const categories = dataObj.values.map((val) => val[0]);
    const series = dataObj.values.map((val) => [`${val[0]}|${val[1]}`, 0, 100]);
    this.chartOptions = {
      ...ColumnRangeChartOptions,
    };
    this.chartOptions.plotOptions.series["pointWidth"] = 24;
    this.chartOptions.plotOptions.series["borderRadius"] = 4;
    this.chartOptions.xAxis["categories"] = categories;
    this.chartOptions.series[0]["data"] = this.setSelectedOnLoad(series);
    this.chartOptions.plotOptions.columnrange.events = {
      click: (event) => this.clickOnChart(event),
    };
  }

  clickOnChart(data) {
    if (data.point.color === ChartStateColors.disabled) {
      return;
    }
    const count = data.point.name.split("|")[1];
    if (parseInt(count) === 0) {
      //Block 0 count click on chart
      return;
    }
    //deselect case
    const id = this.categoryToIdMap[data.point.category];
    if (id === this.chartFilters.phase) {
      this.chartFilters.phase = "";
      this.lastCalled.chart_filter.phase = "";

      this.chartOptions.plotOptions.series.color = ChartStateColors.selected;
      this.chartOptions.colors = [ChartStateColors.selected];
      //deselect all
      const series = this.chartOptions.series[0]["data"];
      this.chartOptions.series[0]["data"] = series.map((val) => {
        if (val.selected) val.selected = false;
        return val;
      });
      this.updateFlag = true;
      this.store.dispatch(
        new FilterEventChanged({
          filterChanged: "chart",
          source: "phase-deselected",
          data: {
            chartFilters: this.chartFilters,
          },
        }),
      );
      return;
    }
    //update filter and emit event for other charts
    // this.chartFilters.phase = data.point.category;
    this.lastCalled.chart_filter.phase = id;
    this.chartFilters = JSON.parse(
      JSON.stringify({
        ...this.chartFilters,
        phase: id,
      }),
    );
    this.store.dispatch(
      new FilterEventChanged({
        filterChanged: "chart",
        source: "phase-selected",
        data: {
          chartFilters: this.chartFilters,
          extraInfo: {
            phase_data: {
              [id]: data.point.category,
            },
          },
        },
      }),
    );
    //change color of unselected bars
    this.chartOptions.plotOptions.series.color = ChartStateColors.deselected;
    this.chartOptions.colors = [ChartStateColors.deselected];
    this.updateFlag = true;
  }

  setSelectedOnLoad(series) {
    const newSeries = series.map((val, index) => {
      const phaseName = val[0].split("|")[0];
      const count = val[0].split("|")[1];
      const obj = {
        x: index,
        low: 0,
        high: 100,
        name: val[0],
        selected: false,
        disabled: false,
        class: "phase-point",
      };

      if (count === "0") {
        obj["color"] = ChartStateColors.disabled;
        obj.disabled = true;
      }
      if (this.chartFilters.phase) {
        const id = this.categoryToIdMap[phaseName];
        if (id === this.chartFilters.phase) {
          obj.selected = true;
          return obj;
        }
      }
      return obj;
    });
    if (this.chartFilters.phase) {
      this.chartOptions.plotOptions.series.color = ChartStateColors.deselected;
    } else {
      this.chartOptions.plotOptions.series.color = ChartStateColors.selected;
    }

    return newSeries;
  }

  ngOnDestroy() {
    if (this.dataCallSubscription) {
      this.dataCallSubscription.unsubscribe();
    }
    if (this.pharmaDatabasePageSubscriber) {
      this.pharmaDatabasePageSubscriber.unsubscribe();
    }
  }
}
